import { AUTH_TOKEN_KEY } from '/src/features/_global/constants';

const DEFAULT_REFRESH_TIMEOUT_MS = 8000;

let refreshRequester = null;
let refreshInFlight = null;

function getAccessToken(tokenPayload) {
  if (typeof tokenPayload === 'string') {
    return tokenPayload;
  }

  return tokenPayload?.access_token ?? null;
}

export function getStoredAccessToken() {
  return window.localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredAccessToken(token) {
  if (!token || token === 'undefined' || token === 'null') {
    return;
  }

  window.localStorage.setItem(AUTH_TOKEN_KEY, token);
}

export function clearStoredAccessToken() {
  window.localStorage.removeItem(AUTH_TOKEN_KEY);
}

export function registerTokenRefreshRequester(requester) {
  refreshRequester = typeof requester === 'function' ? requester : null;

  return () => {
    if (refreshRequester === requester) {
      refreshRequester = null;
    }
  };
}

export function requestTokenRefresh({ timeoutMs = DEFAULT_REFRESH_TIMEOUT_MS } = {}) {
  if (refreshInFlight) {
    return refreshInFlight.promise;
  }

  if (typeof refreshRequester !== 'function') {
    return Promise.reject(new Error('Token refresh requester is not registered'));
  }

  let timerId;
  let resolvePromise;
  let rejectPromise;

  const promise = new Promise((resolve, reject) => {
    resolvePromise = resolve;
    rejectPromise = reject;
  }).finally(() => {
    if (refreshInFlight?.timerId) {
      window.clearTimeout(refreshInFlight.timerId);
    }
    refreshInFlight = null;
  });

  timerId = window.setTimeout(() => {
    rejectTokenRefresh(new Error('Token refresh timed out'));
  }, timeoutMs);

  refreshInFlight = {
    promise,
    resolve: resolvePromise,
    reject: rejectPromise,
    timerId,
  };

  try {
    refreshRequester();
  } catch (error) {
    rejectTokenRefresh(error);
  }

  return promise;
}

export function resolveTokenRefresh(tokenPayload) {
  if (!refreshInFlight) {
    return;
  }

  const accessToken = getAccessToken(tokenPayload);
  if (!accessToken) {
    return;
  }

  refreshInFlight.resolve(tokenPayload);
}

export function rejectTokenRefresh(error = new Error('Token refresh failed')) {
  if (!refreshInFlight) {
    return;
  }

  refreshInFlight.reject(error);
}
